import axios from 'axios';
import { enqueue } from './offlineQueue';

// Use relative URLs - frontend and backend are always on the same domain in Emergent deployments
const API_URL = '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 45000,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Retry config
const MAX_RETRIES = 3;
const RETRY_DELAYS = [1000, 2500, 5000];
const WRITE_METHODS = ['post', 'put', 'patch', 'delete'];
const IDEMPOTENT_METHODS = ['get', 'head', 'options', 'put', 'delete'];

// ---------------------------------------------------------------------------
// Opt-out from auto-retry + offline queue.
//
// Some requests are intentional "probes" (e.g. POST /audio-summary/__probe__
// to detect whether ElevenLabs is configured) where a 5xx is informational
// rather than a failed user write. Without an opt-out, the interceptor
// auto-retried 3× and then enqueued the failed write — producing the sticky
// "Sincronizando 1 cambio pendiente…" banner across sessions.
//
// Two equivalent ways for callers to mark a request as opt-out:
//
//   // axios extra option
//   api.post('/foo', body, { noQueue: true })
//
//   // explicit header (works through any axios wrapper)
//   api.post('/foo', body, { headers: { 'X-No-Queue': 'true' } })
//
// When opt-out is set:
//   • The auto-retry layer is skipped (no extra 1s/2.5s/5s wait).
//   • The failed request is NOT enqueued in the offline write queue.
// The original error still rejects normally so callers can handle it locally.
// ---------------------------------------------------------------------------

// Handle errors with retry and offline queue
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    if (!config) return Promise.reject(error);
    
    const isNetworkError = !error.response && (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK' || error.message?.includes('Network Error'));
    const isServerError = error.response?.status === 503 || error.response?.status === 502;
    const isRetryable = isNetworkError || isServerError;
    const retryCount = config._retryCount || 0;
    const method = config.method?.toLowerCase();
    const isIdempotent = IDEMPOTENT_METHODS.includes(method);

    // Opt-out: callers can pass `{ headers: { 'X-No-Queue': 'true' } }` or
    // the axios-level shortcut `{ noQueue: true }` on any request that
    // shouldn't be retried offline OR auto-retried on 5xx. Useful for
    // "probe" calls (e.g. a POST that pings whether an integration is
    // configured) where a 5xx is diagnostic, not a real user write.
    const optOut =
      config.noQueue === true ||
      config.headers?.['X-No-Queue'] === 'true' ||
      config.headers?.['x-no-queue'] === 'true';

    // Auto-retry: always for idempotent methods, only on network errors for POST (not yet sent)
    if (!optOut && isRetryable && retryCount < MAX_RETRIES && (isIdempotent || isNetworkError)) {
      config._retryCount = retryCount + 1;
      const delay = RETRY_DELAYS[retryCount] || 5000;
      console.warn(`[Retry ${config._retryCount}/${MAX_RETRIES}] ${method?.toUpperCase()} ${config.url}. Waiting ${delay}ms...`);
      await new Promise(r => setTimeout(r, delay));
      return api(config);
    }

    // All retries exhausted - queue write operations for offline sync.
    //
    // We queue when EITHER:
    //   (a) the server is in distress (503/502), OR
    //   (b) the network is genuinely down (offline) AND the mutation is on
    //       the explicit whitelist of "safely replayable" critical paths.
    //
    // Why a whitelist for (b)?  An offline POST /uploads/photos would
    // produce duplicates on reconnect because the file payload can't be
    // perfectly reproduced; an offline /auth/login would replay credentials
    // after the user already retried manually; etc.  So we only queue the
    // operations the receptionist NEEDS to do during a network blip:
    //   - Creating an appointment
    //   - Changing an appointment status (Llegado / En consulta / Atendido)
    //   - Calling a patient (notify-turn)
    //   - Pushing a signature to a kiosk
    // All of these are idempotent enough that a duplicate replay is either
    // a no-op (status already set) or recoverable (a duplicate appointment
    // is easy to spot and delete).
    const QUEUEABLE_OFFLINE_PATHS = [
      // appointment lifecycle (create + status changes)
      /^\/appointments\/?$/,                                   // POST create
      /^\/appointments\/[^/]+\/status(\?|$)/,                  // PUT status
      /^\/appointments\/[^/]+\/notify-turn$/,                  // POST call patient
      /^\/appointments\/[^/]+\/revert-status$/,                // PUT revert
      // kiosk pushes
      /^\/kiosk\/devices\/[^/]+\/push$/,
    ];

    const path = (config.url || '').split('?')[0];
    const isWhitelistedMutation =
      WRITE_METHODS.includes(method) &&
      QUEUEABLE_OFFLINE_PATHS.some((re) => re.test(path));

    const shouldQueue =
      !optOut &&
      (
        (isServerError && WRITE_METHODS.includes(method)) ||
        (isNetworkError && isWhitelistedMutation)
      );

    if (shouldQueue) {
      try {
        const data = config.data ? (typeof config.data === 'string' ? JSON.parse(config.data) : config.data) : null;
        enqueue({
          method: config.method.toUpperCase(),
          url: config.baseURL + config.url,
          data,
          description: `${config.method?.toUpperCase()} ${config.url}`,
        });
        // For offline-queued operations we return a "synthetic 202 Accepted"
        // so the calling component can display a friendly toast instead of
        // an error. The actual replay happens via offlineQueue.syncQueue()
        // when the connection returns.
        if (isNetworkError && isWhitelistedMutation) {
          window.dispatchEvent(new CustomEvent('offline-queued', {
            detail: { path, method: config.method.toUpperCase() }
          }));
          return Promise.resolve({
            data: { queued: true, offline: true, message: 'Cambio guardado localmente, se sincronizará al recuperar la conexión.' },
            status: 202,
            statusText: 'Accepted (queued offline)',
            headers: {},
            config,
            request: error.request,
          });
        }
      } catch (e) { /* ignore queue errors */ }
    }

    if (error.response?.status === 401) {
      // Only treat 401 as "token invalid" when the user is actually inside
      // the admin console. Public pages (booking, /cumplimiento, /derecho-
      // al-olvido, /presupuesto/aceptar/*) must NEVER wipe the admin token
      // from localStorage — background calls from a public tab opened
      // alongside the admin tab would otherwise log the user out from the
      // other tab. The 2026-05-24 incident root-caused this exact pattern.
      //
      // Additional carve-out: a 401 on /auth/refresh is treated as "best-
      // effort refresh failed" — the still-valid short-lived access token
      // is left untouched. The next user-driven call decides definitively
      // whether the session is still alive.
      const path = window.location.pathname || '/';
      const url = String(config.url || '');
      const inAdminRoute = path.startsWith('/admin');
      const isLoginPage = path === '/admin/login';
      const isRefreshCall = url.endsWith('/auth/refresh');
      if (inAdminRoute && !isLoginPage && !isRefreshCall) {
        localStorage.removeItem('token');
        window.location.href = '/admin/login';
      } else if (!inAdminRoute) {
        // Public route — surface in console but don't touch the admin token.
        console.warn('[api] 401 on public route — leaving admin token intact:', url);
      }
    }
    // 409 Conflict - optimistic locking conflict
    if (error.response?.status === 409 && typeof error.response?.data?.detail === 'string') {
      window.dispatchEvent(new CustomEvent('appointment-conflict', {
        detail: { message: error.response.data.detail }
      }));
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (email, password, totp_code = null, use_recovery_code = false) => 
    api.post('/auth/login', { email, password, totp_code, use_recovery_code }),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  revokeAll: () => api.post('/auth/revoke-all'),
  // PIN management
  pinSetup: (pin) => api.post('/auth/pin/setup', { pin }),
  pinVerify: (pin) => api.post('/auth/pin/verify', { pin }),
  pinStatus: () => api.get('/auth/pin/status'),
};

export const securityAPI = {
  getAutoLogoutPolicy: () => api.get('/security/auto-logout-policy'),
  revokeAdminTokens: (adminId) => api.post(`/admins/${adminId}/revoke-tokens`),
};

export const rgpdAPI = {
  listBreaches: () => api.get('/rgpd/breaches'),
  pendingAepdAlert: () => api.get('/rgpd/breaches/pending-aepd-alert'),
  createBreach: (data) => api.post('/rgpd/breaches', data),
  updateBreach: (id, data) => api.patch(`/rgpd/breaches/${id}`, data),
  deleteBreach: (id) => api.delete(`/rgpd/breaches/${id}`),
  getResponsible: () => api.get('/rgpd/responsible'),
  saveResponsible: (data) => api.put('/rgpd/responsible', data),
  generateAepdPdf: (id) => api.get(`/rgpd/breaches/${id}/aepd-pdf`, { responseType: 'blob' }),
};

// RGPD legal documentation registry (Art. 30, 35, etc.) — operator-managed
// inventory of administrative templates.
export const rgpdDocsAPI = {
  list: () => api.get('/admin/rgpd-docs'),
  update: (key, data) => api.put(`/admin/rgpd-docs/${key}`, data),
  clear: (key) => api.delete(`/admin/rgpd-docs/${key}`),
  getTemplate: (key) => api.get(`/admin/rgpd-docs/${key}/template`),
  downloadTemplate: (key) => api.get(`/admin/rgpd-docs/${key}/download`, { responseType: 'blob' }),
  reviewStatus: () => api.get('/admin/rgpd-docs/review-reminder/status'),
  runReviewReminder: () => api.post('/admin/rgpd-docs/review-reminder/run-now'),
  getClinicData: () => api.get('/admin/rgpd-docs/clinic-data'),
  updateClinicData: (data) => api.put('/admin/rgpd-docs/clinic-data', data),
  generatePdf: (key) => api.get(`/admin/rgpd-docs/${key}/pdf`, { responseType: 'blob' }),
  generateAndPublishAll: () => api.post('/admin/rgpd-docs/generate-and-publish-all'),
  urlRenewerStatus: () => api.get('/admin/rgpd-docs/url-renewer/status'),
  runUrlRenewer: (forceAll = false) =>
    api.post(`/admin/rgpd-docs/url-renewer/run-now${forceAll ? '?force_all=true' : ''}`),
};

// Location APIs
export const locationAPI = {
  getAll: () => api.get('/locations'),
  getActive: () => api.get('/locations/active'),
  getPublic: () => api.get('/locations/public'),
  create: (data) => api.post('/locations', data),
  update: (id, data) => api.put(`/locations/${id}`, data),
  toggleBookingVisibility: (id) => api.patch(`/locations/${id}/booking-visibility`),
  delete: (id) => api.delete(`/locations/${id}`),
};

// Admin Profile APIs
export const adminProfileAPI = {
  get: () => api.get('/admin/profile'),
  update: (data) => api.put('/admin/profile', data),
};

// 2FA APIs
export const twoFactorAPI = {
  getStatus: () => api.get('/admin/2fa/status'),
  setup: () => api.post('/admin/2fa/setup'),
  enable: (code) => api.post('/admin/2fa/enable', { code }),
  disable: (code) => api.post('/admin/2fa/disable', { code }),
  regenerateRecoveryCodes: (code) => api.post('/admin/2fa/regenerate-recovery-codes', { code }),
};

// Insurance APIs
export const insuranceAPI = {
  getAll: () => api.get('/insurances'),
  getActive: () => api.get('/insurances/active'),
  getPublic: () => api.get('/insurances/public'),
  create: (data) => api.post('/insurances', data),
  update: (id, data) => api.put(`/insurances/${id}`, data),
  delete: (id) => api.delete(`/insurances/${id}`),
};

// Slot APIs
export const healthCheckAPI = {
  run: (staleBudgetDays = 60) => api.get('/admin/health-check', { params: { stale_budget_days: staleBudgetDays } }),
};

export const systemHealthAPI = {
  get: () => api.get('/admin/system-health'),
  restartBackend: () => api.post('/admin/system-health/restart-backend'),
  backfillHasMedicalRecord: () => api.post('/admin/backfill/has-medical-record'),
};

export const slotAPI = {
  getAll: (params) => api.get('/slots', { params }),
  getAvailable: (params) => api.get('/slots/available', { params }),
  getCalendar: (month, year, locationId, visitType) => api.get('/slots/calendar', { 
    params: { month, year, location_id: locationId, visit_type: visitType } 
  }),
  getAdminCalendar: (month, year, locationId, doctorId) => api.get('/slots/admin-calendar', { 
    params: { month, year, location_id: locationId, doctor_id: doctorId } 
  }),
  getByDate: (date, locationId, doctorId) => api.get('/slots/by-date', { 
    params: { date, location_id: locationId, doctor_id: doctorId } 
  }),
  create: (data) => api.post('/slots', data),
  createBulk: (data) => api.post('/slots/bulk', data),
  update: (id, data) => api.put(`/slots/${id}`, data),
  updateDuration: (id, data) => api.put(`/slots/${id}/duration`, data),
  extendDuration: (id, newEndTime) => api.put(`/slots/${id}/extend-duration`, { new_end_time: newEndTime }),
  block: (id, reason) => api.put(`/slots/${id}/block`, { reason: reason || '' }),
  unblock: (id) => api.put(`/slots/${id}/unblock`),
  bulkBlockPreview: (data) => api.post('/slots/bulk-block-preview', data),
  bulkBlock: (data) => api.post('/slots/bulk-block', data),
  blockedSummary: () => api.get('/slots/blocked-summary'),
  auditConsistency: () => api.get('/slots/audit-consistency'),
  normalizeConsistency: (opts = {}) => api.post('/slots/normalize-consistency', {
    fix_booked_but_available: opts.fix_booked_but_available ?? true,
    fix_orphaned_unavailable: opts.fix_orphaned_unavailable ?? false,
    fix_duplicate_slots: opts.fix_duplicate_slots ?? false,
  }),
  delete: (id) => api.delete(`/slots/${id}`),
  // External slots
  createExternal: (data) => api.post('/slots/external', data),
  getExternal: (params) => api.get('/slots/external', { params }),
  updateExternal: (id, data) => api.put(`/slots/external/${id}`, data),
  deleteExternal: (id) => api.delete(`/slots/external/${id}`),
  importExternalCSV: (locationId, source, rows) => api.post('/slots/external/import-csv', rows, { 
    params: { location_id: locationId, source } 
  }),
  getExternalSources: () => api.get('/slots/external/sources'),
  getDoctorLocations: (doctorId) => api.get(`/slots/doctor-locations/${doctorId}`),
};

// Appointment APIs
export const appointmentAPI = {
  getAll: async (params) => {
    const resp = await api.get('/appointments', { params: { ...params, page_size: 500 } });
    // Unwrap paginated response for backwards compatibility
    if (resp.data && resp.data.appointments) {
      resp.data = resp.data.appointments;
    }
    return resp;
  },
  getPaginated: (params) => api.get('/appointments', { params }),
  getOne: (id) => api.get(`/appointments/${id}`),
  create: (data) => api.post('/appointments', data),
  getRescheduleContext: (appointmentId) => api.get(`/appointments/${appointmentId}/reschedule-context`),
  getRescheduleCandidates: (appointmentId) => api.get(`/appointments/${appointmentId}/reschedule-candidates`),
  markRescheduled: (appointmentId, newAppointmentId) => api.post(`/appointments/${appointmentId}/mark-rescheduled`, { new_appointment_id: newAppointmentId }),
  cancelRescheduleRequest: (appointmentId) => api.post(`/appointments/${appointmentId}/cancel-reschedule-request`),
  resolveRescheduleRequest: (appointmentId) => api.post(`/appointments/${appointmentId}/resolve-reschedule-request`),
  createManual: (data) => api.post('/admin/appointments', data),
  createAdmin: (data) => api.post('/admin/appointments', data),
  update: (id, data) => api.put(`/appointments/${id}`, data),
  updateStatus: (id, status, options = {}) => {
    const params = new URLSearchParams({ status });
    if (options.notify_email !== undefined) params.append('notify_email', options.notify_email);
    if (options.notify_whatsapp !== undefined) params.append('notify_whatsapp', options.notify_whatsapp);
    return api.put(`/appointments/${id}/status?${params.toString()}`);
  },
  confirm: (id, options = {}) => api.post(`/appointments/${id}/confirm`, options),
  requestConfirmation: (id, options = {}) => api.post(`/appointments/${id}/request-confirmation`, options),
  updateReminders: (id, reminders = []) => api.put(`/appointments/${id}/reminders`, { reminders }),
  confirmationSummary: () => api.get('/appointments/confirmation-summary'),
  cancel: (id) => api.put(`/appointments/${id}/status?status=cancelled`),
  cancelByPatient: (id) => api.put(`/appointments/${id}/status?status=cancelled&cancelled_by=patient`),
  delete: (id) => api.delete(`/appointments/${id}`),
  changeDuration: (id, newEndTime) => api.put(`/appointments/${id}/duration`, { new_end_time: newEndTime }),
  // Notify patient that it's their turn
  notifyTurn: (id, options = {}) => api.post(`/appointments/${id}/notify-turn`, {
    send_sms: options.send_sms || false,
    send_whatsapp: options.send_whatsapp || false,
    send_email: options.send_email || false,
    custom_message: options.custom_message || null
  }),
  // Undo last status change (one level)
  revertStatus: (id) => api.post(`/appointments/${id}/revert-status`),
  // Public endpoints for patient cancellation/modification
  getByToken: (token) => api.get(`/appointment/by-token/${token}`),
  cancelByToken: (token, cancellationReason = null) => api.post(
    `/appointment/cancel-by-token/${token}`,
    cancellationReason ? { cancellation_reason: cancellationReason } : {}
  ),
  getAvailableSlotsForModify: (token) => api.get(`/appointment/available-slots-for-modify/${token}`),
  modifyByToken: (token, newSlotId) => api.post(`/appointment/modify-by-token/${token}`, { new_slot_id: newSlotId }),
  getNextForPatient: (patientId) => api.get(`/appointments/patient/${patientId}/next`),
  reschedule: (id, data) => api.put(`/appointments/${id}`, {
    location_id: data.new_location_id,
    date: data.new_date,
    start_time: data.new_start_time,
    end_time: data.new_end_time,
  }),
};

// Checkout APIs (Stripe for teleconsulta)
export const checkoutAPI = {
  createTeleconsulta: (data) => api.post('/checkout/teleconsulta', { ...data, origin_url: window.location.origin }),
  getStatus: (sessionId) => api.get(`/checkout/status/${sessionId}`),
  cancel: (appointmentId) => api.post(`/checkout/cancel/${appointmentId}`),
};

// Billing APIs
export const billingAPI = {
  getMonthly: (year, locationId, doctorId) => api.get('/billing/monthly', { params: { year, location_id: locationId, doctor_id: doctorId } }),
  getSummary: (year, month, locationId, doctorId) => api.get('/billing/summary', { params: { year, month, location_id: locationId, doctor_id: doctorId } }),
  getAppointments: (year, month, locationId, insuranceId, doctorId) => 
    api.get('/billing/appointments', { params: { year, month, location_id: locationId, insurance_id: insuranceId, doctor_id: doctorId } }),
  getSurgeries: (year, month, locationId, insuranceId, doctorId) => 
    api.get('/billing/surgeries', { params: { year, month, location_id: locationId, insurance_id: insuranceId, doctor_id: doctorId } }),
  getCombined: (year, month, locationId, insuranceId, doctorId) => 
    api.get('/billing/combined', { params: { year, month, location_id: locationId, insurance_id: insuranceId, doctor_id: doctorId } }),
  getByLocation: (locationId, year, month, doctorId) => 
    api.get(`/billing/by-location/${locationId}`, { params: { year, month, doctor_id: doctorId } }),
  exportExcel: (year, month, locationId, insuranceId, doctorId) => 
    api.get('/billing/export/excel', { 
      params: { year, month, location_id: locationId, insurance_id: insuranceId, doctor_id: doctorId },
      responseType: 'blob'
    }),
  exportPdf: (year, month, locationId, insuranceId, doctorId) => 
    api.get('/billing/export/pdf', { 
      params: { year, month, location_id: locationId, insurance_id: insuranceId, doctor_id: doctorId },
      responseType: 'blob'
    }),
};

// Google Calendar APIs - used by SettingsPage for OAuth flow
// Actual implementation is defined at the bottom of this file

// SMS & WhatsApp APIs
export const notificationAPI = {
  getTwilioStatus: () => api.get('/notifications/twilio-status'),
  sendSMS: (data) => api.post('/notifications/send-sms', data),
};

// Messaging (Twilio SMS + WhatsApp) — admin panel
export const messagingAPI = {
  getStatus: () => api.get('/messaging/status'),
  sendTest: (data) => api.post('/messaging/send-test', data),
  getLogs: (params = {}) => api.get('/messaging/logs', { params }),
  getInbox: (params = {}) => api.get('/messaging/inbox', { params }),
  getInboxUnreadCount: () => api.get('/messaging/inbox/unread-count'),
  markInboxRead: (id) => api.post(`/messaging/inbox/${id}/read`),
  markAllInboxRead: () => api.post('/messaging/inbox/read-all'),
  deleteInboxMessage: (id) => api.delete(`/messaging/inbox/${id}`),
  getConversations: () => api.get('/messaging/conversations'),
  getConversation: (key) => api.get(`/messaging/conversations/${key}`),
  markConversationRead: (key) => api.post(`/messaging/conversations/${key}/read`),
  sendConversationReply: (key, body) => api.post(`/messaging/conversations/${key}/send`, { body }),
  getAssistantTexts: () => api.get('/messaging/assistant-texts'),
  saveAssistantTexts: (texts) => api.put('/messaging/assistant-texts', { texts }),
  getAssistantPoster: () => api.get('/messaging/assistant-poster', { responseType: 'blob' }),
  getAssistantStory: () => api.get('/messaging/assistant-story', { responseType: 'blob' }),
  getAssistantStats: () => api.get('/messaging/assistant-stats'),
  getAssistantFaqs: () => api.get('/messaging/assistant-faqs'),
  saveAssistantFaqs: (faqs) => api.put('/messaging/assistant-faqs', { faqs }),
  getAnnouncementCampaign: () => api.get('/messaging/announcement-campaign'),
  sendAnnouncementCampaign: (channels) => api.post('/messaging/announcement-campaign/send', { channels, confirm: true }),
  grantEmailToAll: () => api.post('/users/grant-email-permission-to-all'),
};

// ─────────────────────────────────────────────────────────────────────────
// SMS share-link delivery for clinical PDFs.
// One generic method per document type — all share the same backend pattern.
// ─────────────────────────────────────────────────────────────────────────
export const documentShareAPI = {
  sendPrescription: (id, overridePhone = null) =>
    api.post(`/prescriptions/${id}/send-sms`, { override_phone: overridePhone }),
  sendConsent: (id, overridePhone = null) =>
    api.post(`/consents/${id}/send-sms`, { override_phone: overridePhone }),
  sendCertificate: (id, overridePhone = null) =>
    api.post(`/medical-certificates/${id}/send-sms`, { override_phone: overridePhone }),
  sendBudget: (id, overridePhone = null) =>
    api.post(`/surgery-budgets/${id}/send-sms`, { override_phone: overridePhone }),
  sendMedicalReport: (id, overridePhone = null) =>
    api.post(`/medical-reports/${id}/send-sms`, { override_phone: overridePhone }),
  // Patient portal (permanent URL with all docs).
  getPortalUrl: (patientId) => api.get(`/patients/${patientId}/portal/url`),
  sendPortalSms: (patientId) => api.post(`/patients/${patientId}/portal/send-sms`),
};

// Clinic profile (used to personalise outbound messages)
export const clinicProfileAPI = {
  get: () => api.get('/clinic-profile'),
  update: (data) => api.put('/clinic-profile', data),
  getDefaults: () => api.get('/clinic-profile/defaults'),
};

// Surgical Procedures APIs
export const procedureAPI = {
  getAll: () => api.get('/procedures'),
  getActive: () => api.get(`/procedures/active?_t=${Date.now()}`, { 
    headers: { 'Cache-Control': 'no-cache' } 
  }),
  getOne: (id) => api.get(`/procedures/${id}`),
  create: (data) => api.post('/procedures', data),
  update: (id, data) => api.put(`/procedures/${id}`, data),
  delete: (id) => api.delete(`/procedures/${id}`),
};

// Surgeries APIs
export const surgeryAPI = {
  getAll: (params) => api.get('/surgeries', { params }),
  getByPatient: (patientId) => api.get('/surgeries', { params: { patient_id: patientId } }),
  getCalendar: (month, year, locationId) => 
    api.get('/surgeries/calendar', { params: { month, year, location_id: locationId } }),
  getOne: (id) => api.get(`/surgeries/${id}`),
  create: (data) => api.post('/surgeries', data),
  update: (id, data) => api.put(`/surgeries/${id}`, data),
  updateStatus: (id, status) => api.patch(`/surgeries/${id}/status`, null, { params: { status } }),
  updatePaymentStatus: (id, paymentStatus) => api.patch(`/surgeries/${id}/payment-status`, null, { params: { payment_status: paymentStatus } }),
  delete: (id) => api.delete(`/surgeries/${id}`),
  exportExcel: (params) => api.get('/surgeries/export/excel', { params, responseType: 'blob' }),
  exportPdf: (params) => api.get('/surgeries/export/pdf', { params, responseType: 'blob' }),
};

// Patients APIs
export const patientAPI = {
  getAll: (search, location_id, page = 1, page_size = 50, doctor_id, surname_letter, stale_filter, reminder_age_filter, waitlist_opted_out) => api.get('/patients', { params: { search, location_id, page, page_size, doctor_id, surname_letter, stale_filter, reminder_age_filter, waitlist_opted_out: waitlist_opted_out ? true : undefined } }).then(res => {
    // Normalize: always ensure res.data has .patients array for paginated consumers
    // and res.data is iterable for legacy consumers using .slice()
    const d = res.data;
    if (Array.isArray(d)) {
      res.data = { patients: d, total: d.length, page: 1, total_pages: 1 };
    }
    // Add array-like access for legacy code that does response.data.slice()
    if (!Array.isArray(res.data) && res.data.patients) {
      const orig = res.data;
      const arr = orig.patients;
      arr.total = orig.total;
      arr.page = orig.page;
      arr.total_pages = orig.total_pages;
      // Store both formats
      res._paginated = orig;
      res.data = arr;
    }
    return res;
  }),
  search: (q) => api.get('/patients/search', { params: { q } }),
  getOne: (id) => api.get(`/patients/${id}`),
  create: (data) => api.post('/patients', data),
  update: (id, data) => api.put(`/patients/${id}`, data),
  delete: (id) => api.delete(`/patients/${id}`),
  exportExcel: (params) => api.get('/patients/export/excel', { params, responseType: 'blob' }),
  exportPdf: (params) => api.get('/patients/export/pdf', { params, responseType: 'blob' }),
  // Filtered CSV export — same filter set as `getAll`. Used for handing
  // the "personal-call shortlist" to non-admin staff (Feb 2026).
  exportCsv: (search, location_id, doctor_id, surname_letter, stale_filter, reminder_age_filter) => api.get('/patients/export.csv', {
    params: { search, location_id, doctor_id, surname_letter, stale_filter, reminder_age_filter },
    responseType: 'blob',
  }),
  // New methods for saving patient from appointments/surgeries
  checkExists: (email, phone) => api.get('/patients/check-exists', { params: { email, phone } }),
  checkHomonyms: (name, exclude_id) => api.get('/patients/homonyms', { params: { name, exclude_id } }),
  auditHomonymResolution: (payload) => api.post('/patients/homonyms/audit', payload),
  saveFromAppointment: (data) => api.post('/patients/save-from-appointment', data),
  // Fix surgery counter issues
  recalculateSurgeries: (id) => api.post(`/patients/${id}/recalculate-surgeries`),
  // Duplicate management
  getAllDuplicates: () => api.get('/patients/admin/all-duplicates'),
  getDniAudit: () => api.get('/patients/admin/dni-audit'),
  mergePatients: (data) => api.post('/patients/admin/merge', data),
  undoMerge: (merge_id) => api.post(`/patients/admin/merge/undo/${merge_id}`),
  mergeHistory: (limit = 50) => api.get(`/patients/admin/merge/history?limit=${limit}`),
  dismissDuplicate: (patient_id_1, patient_id_2) => api.post('/patients/admin/dismiss-duplicate', { patient_id_1, patient_id_2 }),
  getDuplicateRadar: () => api.get('/patients/admin/duplicate-radar'),
  refreshDuplicateRadar: () => api.post('/patients/admin/duplicate-radar/refresh'),
  checkEditDuplicates: (id, data) => api.post(`/patients/${id}/check-edit-duplicates`, data),
  assignDoctor: (id, doctor_id, reason) => api.put(`/patients/${id}/assign-doctor`, { doctor_id, reason }),
};

// Users Management APIs (admin only)
export const userAPI = {
  getAll: () => api.get('/users'),
  // List of selectable doctors — the canonical /doctors endpoint already
  // includes the main superadmin (Dr. Marcos Adrianzén) on top of every
  // user with role=doctor. Use this instead of /users.filter(role===doctor)
  // anywhere a "Doctor" dropdown is needed; otherwise the superadmin is
  // silently excluded.
  getDoctors: () => api.get('/doctors'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  resendWelcome: (id) => api.post(`/users/${id}/resend-welcome`),
  // Custom permission templates saved by the superadmin (per-clinic).
  listCustomTemplates: () => api.get('/permission-templates/custom'),
  createCustomTemplate: (data) => api.post('/permission-templates/custom', data),
  deleteCustomTemplate: (id) => api.delete(`/permission-templates/custom/${id}`),
};

// Superadmin "Simular como…" (impersonation)
export const impersonationAPI = {
  candidates: () => api.get('/admin/impersonate/candidates'),
  start: (targetId) => api.post(`/admin/impersonate/${targetId}`),
};

// Priority Waitlist APIs
export const waitlistAPI = {
  list: (params = {}) => api.get('/waitlist', { params }),
  create: (data) => api.post('/waitlist', data),
  update: (id, data) => api.patch(`/waitlist/${id}`, data),
  remove: (id) => api.delete(`/waitlist/${id}`),
  matches: (appointmentId) => api.get(`/waitlist/matches/${appointmentId}`),
  notify: (entryId, appointmentId) => api.post(`/waitlist/${entryId}/notify/${appointmentId}`),
  pendingCount: () => api.get('/waitlist/pending-count'),
  rehydrate: () => api.post('/waitlist/rehydrate'),
  triggerMatcher: (appointmentId) => api.post(`/waitlist/trigger-matcher/${appointmentId}`),
};

// Doctor APIs
export const doctorAPI = {
  getAll: () => api.get('/doctors'),
};

// Weekly Summary API
export const weeklySummaryAPI = {
  get: (weekStart, doctorId) => api.get('/weekly-summary', { params: { week_start: weekStart, doctor_id: doctorId } }),
  analytics: (weekStart, doctorId) => api.get('/weekly-summary/analytics', { params: { week_start: weekStart, doctor_id: doctorId } }),
};

// Expense APIs
export const expenseAPI = {
  // Categories
  getCategories: () => api.get('/expenses/categories'),
  createCategory: (data) => api.post('/expenses/categories', data),
  updateCategory: (id, data) => api.put(`/expenses/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/expenses/categories/${id}`),
  
  // Monthly Expenses
  getMonthly: (year, month, doctorId) => api.get('/expenses/monthly', { params: { year, month, doctor_id: doctorId || undefined } }),
  createMonthly: (data) => api.post('/expenses/monthly', data),
  updateMonthly: (id, data) => api.put(`/expenses/monthly/${id}`, data),
  deleteMonthly: (id) => api.delete(`/expenses/monthly/${id}`),
  confirmMonthly: (id) => api.post(`/expenses/monthly/${id}/confirm`),
  
  // Recurring (Fixed) Expenses
  getRecurring: (doctorId) => api.get('/expenses/recurring', { params: doctorId ? { doctor_id: doctorId } : {} }),
  createRecurring: (data) => api.post('/expenses/recurring', data),
  updateRecurring: (id, data) => api.put(`/expenses/recurring/${id}`, data),
  deleteRecurring: (id) => api.delete(`/expenses/recurring/${id}`),
  generateRecurring: (year, month) => api.post(`/expenses/recurring/generate?year=${year}&month=${month}`),
  
  // Profitability
  getProfitability: (year, month, doctorId) => api.get('/expenses/profitability', { params: { year, month, doctor_id: doctorId || undefined } }),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: (doctorId) => api.get('/dashboard/stats', { params: { doctor_id: doctorId || undefined } }),
  getFeed: () => api.get('/dashboard/feed'),
  getAdvancedStats: (year, doctorId) => api.get('/dashboard/advanced-stats', { params: { year, doctor_id: doctorId || undefined } }),
  getAnalytics: (year, doctorId, compareYear) => api.get('/dashboard/analytics', { params: { year, doctor_id: doctorId || undefined, compare_year: compareYear || undefined } }),
  getDoctorStats: () => api.get('/dashboard/doctor-stats'),
  exportAnalyticsExcel: (year) => api.get('/analytics/export/excel', { 
    params: { year },
    responseType: 'blob'
  }),
  exportAnalyticsPdf: (year) => api.get('/analytics/export/pdf', { 
    params: { year },
    responseType: 'blob'
  }),
};

// File Categories API
export const fileCategoriesAPI = {
  getAll: () => api.get('/settings/file-categories'),
  create: (data) => api.post('/settings/file-categories', data),
  update: (id, data) => api.put(`/settings/file-categories/${id}`, data),
  delete: (id) => api.delete(`/settings/file-categories/${id}`),
  setPatientAppEnabled: (id, enabled) => api.put(`/settings/file-categories/${id}/patient-app`, { enabled }),
};

// Medical Records API
export const medicalRecordsAPI = {
  // Historia clínica base
  create: (data) => api.post('/medical-records', data),
  get: (patientId) => api.get(`/medical-records/${patientId}`),
  update: (patientId, data) => api.put(`/medical-records/${patientId}`, data),
  
  // Notas clínicas
  createNote: (data) => api.post('/clinical-notes', data),
  getNote: (noteId, { includeHistory = false } = {}) =>
    api.get(`/clinical-notes/${noteId}`, {
      params: includeHistory ? { include_history: true } : undefined,
    }),
  getNoteBySurgery: (surgeryId) => api.get(`/clinical-notes/by-surgery/${surgeryId}`),
  updateNote: (noteId, data, reason) => api.put(`/clinical-notes/${noteId}`, data, { params: { reason } }),
  deleteNote: (noteId, reason) => api.delete(`/clinical-notes/${noteId}`, { data: { reason } }),
  getNoteVersion: (noteId, version) => api.get(`/clinical-notes/${noteId}/history/${version}`),
  getDeletedNotesLog: () => api.get('/deleted-notes-log'),
  emailNote: (noteId, params) => api.post(`/clinical-notes/${noteId}/email`, null, { params }),
  signNote: (noteId, pin, logo_template, report_id) => api.post(`/clinical-notes/${noteId}/sign`, { pin, logo_template, report_id }),
  unlockNote: (noteId, pin) => api.post(`/clinical-notes/${noteId}/unlock`, { pin }),
  getSignedPdfUrl: (noteId) => `/api/clinical-notes/${noteId}/signed-pdf`,
  emailClinicalHistory: (patientId, params) => api.post(`/patients/${patientId}/clinical-history/email`, null, { params }),
  
  // Macros de texto
  getMacros: (params) => api.get('/text-macros', { params: typeof params === 'string' ? { category: params } : params }),
  createMacro: (data) => api.post('/text-macros', data),
  updateMacro: (macroId, data) => api.put(`/text-macros/${macroId}`, data),
  deleteMacro: (macroId) => api.delete(`/text-macros/${macroId}`),
  duplicateMacro: (macroId) => api.post(`/text-macros/${macroId}/duplicate`),
  copyMacroToField: (macroId, payload) => api.post(`/text-macros/${macroId}/copy-to-field`, payload),
  
  // Búsqueda CIE-10
  searchCIE10: (q) => api.get('/cie10/search', { params: { q } }),
  getCIE10Favorites: () => api.get('/cie10/favorites'),
  addCIE10Favorite: (code, description) => api.post('/cie10/favorites', null, { params: { code, description } }),
  removeCIE10Favorite: (code) => api.delete(`/cie10/favorites/${code}`),

  // CIE-10 AI favorites — independent list per doctor, cross-deduped vs manual.
  getAiDiagnosisFavorites: () => api.get('/cie10/ai-favorites'),
  addAiDiagnosisFavorite: (payload) => api.post('/cie10/ai-favorites', payload),
  removeAiDiagnosisFavorite: (code) => api.delete(`/cie10/ai-favorites/${code}`),
  touchAiDiagnosisFavorite: (code) => api.post(`/cie10/ai-favorites/${code}/touch`),

  searchProcedures: (q) => api.get('/procedures/search', { params: { q } }),
  
  // Schema templates (Clinical Schema)
  getSchemaTemplates: () => api.get('/schema-templates'),
  createSchemaTemplate: (data) => api.post('/schema-templates', data),
  updateSchemaTemplate: (id, data) => api.put(`/schema-templates/${id}`, data),
  deleteSchemaTemplate: (id) => api.delete(`/schema-templates/${id}`),
  uploadSchemaImage: (formData) => api.post('/schema-templates/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // Archivos médicos
  uploadFile: (formData, onProgress) => api.post('/medical-files/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress
  }),
  uploadChunk: (formData, onProgress) => api.post('/medical-files/upload-chunk', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress
  }),
  getPatientFiles: (patientId, category) => api.get(`/medical-files/patient/${patientId}`, { params: { category } }),
  getNoteFiles: (noteId) => api.get(`/medical-files/note/${noteId}`),
  updateFile: (fileId, data) => api.put(`/medical-files/${fileId}`, data),
  updateFileMetadata: (fileId, data) => api.put(`/medical-files/${fileId}/metadata`, data),
  reorderFiles: (orderedIds) => api.put('/medical-files/reorder', { ordered_ids: orderedIds }),
  bulkUpdateCategory: (fileIds, category) => api.put('/medical-files/bulk-category', { file_ids: fileIds, category }),
  bulkLinkNote: (fileIds, clinicalNoteId) => api.put('/medical-files/bulk-clinical-note', { file_ids: fileIds, clinical_note_id: clinicalNoteId }),
  updateFileLinks: (fileId, linkedCategories) => api.put(`/medical-files/${fileId}/links`, { linked_categories: linkedCategories }),
  bulkDeleteFiles: (fileIds) => api.post('/medical-files/bulk-delete', { file_ids: fileIds }),
  deleteFile: (fileId) => api.delete(`/medical-files/${fileId}`),
  renameFile: (fileId, filename) => api.put(`/medical-files/${fileId}/rename`, { filename }),
  markFileReviewed: (fileId) => api.put(`/medical-files/${fileId}/review`),
  markAllFilesReviewed: (patientId) => api.put(`/medical-files/review-all/${patientId}`),
  approveQuarantinedFile: (fileId) => api.put(`/medical-files/${fileId}/quarantine/approve`),
  getModerationIncidents: (patientId) => api.get(`/medical-files/moderation-incidents/${patientId}`),
  setModerationBlock: (patientId, blocked) => api.put(`/medical-files/moderation-block/${patientId}`, { blocked }),
  emailFile: (fileId, overrideEmail) => api.post(
    `/medical-files/${fileId}/email`,
    null,
    { params: overrideEmail ? { override_email: overrideEmail } : {} }
  ),
  emailFilesBulk: (fileIds, overrideEmail) => api.post(
    '/medical-files/email-bulk',
    { file_ids: fileIds, override_email: overrideEmail || null }
  ),
  getFileUrl: (fileId) => {
    const token = localStorage.getItem('token');
    return `${API_URL}/medical-files/${fileId}/view${token ? `?token=${token}` : ''}`;
  },
  
  // Generación de informes PDF
  generateReport: (data) => api.post('/reports/generate', data),
  emailReport: (reportId, params) => api.post(`/reports/${reportId}/email`, null, { params }),
  getPatientReports: (patientId) => api.get(`/reports/patient/${patientId}`),
  getReportUrl: (reportId) => `${API_URL}/reports/${reportId}/download`,
  importExternalReport: (patientId, formData, onProgress) => api.post(`/reports/import/${patientId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress
  }),
  // Import clinical records with OCR
  importClinicalRecord: (patientId, formData, onProgress) => api.post(`/clinical-records/import-pdf/${patientId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress
  }),
  saveExtractedText: (importId, formData) => api.post(`/clinical-records/save-extracted/${importId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getOcrStatus: () => api.get('/ocr/status'),
};

// Casuística clínica APIs
export const casuisticaAPI = {
  getSummary: (params) => api.get('/casuistica/summary', { params }),
};

// Performance monitor APIs (superadmin)
export const perfAPI = {
  getStatus: () => api.get('/admin/perf/status'),
  runNow: () => api.post('/admin/perf/run', {}, { timeout: 120000 }),
};

// Backup & Restore APIs
export const backupAPI = {
  getStatus: () => api.get('/backup/status'),
  getHealth: () => api.get('/backup/health'),
  verifyHealth: () => api.post('/backup/health/verify', {}, { timeout: 180000 }),
  createBackup: () => api.post('/backup/create'),
  createIncrementalBackup: () => api.post('/backup/create-incremental'),
  retryBackup: (taskId) => api.post(`/backup/retry/${taskId}`),
  getProgress: (taskId) => api.get(`/backup/progress/${taskId}`),
  downloadBackup: (taskId) => api.get(`/backup/download/${taskId}`, { responseType: 'blob', timeout: 300000 }),
  synologyStatus: () => api.get('/backup/synology/status'),
  synologyTest: () => api.get('/backup/synology/test'),
  synologyTestUpload: () => api.post('/backup/synology/test-upload'),
  synologyDiagnostics: () => api.get('/backup/synology/diagnostics'),
  ackSynologyAlert: () => api.post('/backup/synology/ack-alert'),
  // Local-disk incremental backup (File System Access API).
  // The local zip endpoint runs as an async background task and returns a
  // task_id so the frontend can poll progress + download once ready, just
  // like the regular createBackup flow. This avoids the 524 timeout that
  // happens when the proxy cuts requests >100s.
  planLocalBackup: (manifest) => api.post('/backup/local/plan', manifest),
  startLocalBackup: (patientIds) => api.post('/backup/local/zip', { patient_ids: patientIds }),
  getLastCompletedBackup: () => api.get('/backup/last-completed'),
  getR2DownloadLink: () => api.get('/backup/r2/download-link'),
  verifyLatestR2: () => api.post('/backup/r2/verify-latest'),
  getVerifyProgress: (taskId) => api.get(`/backup/verify/progress/${taskId}`),
  getSchedule: () => api.get('/backup/schedule'),
  updateSchedule: (data) => api.post('/backup/schedule/update', data),
  validateBackup: (file) => {
    // Legacy single-shot validate — kept for backward compatibility.
    // For large files use restoreChunked* below.
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/backup/validate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  restoreBackup: (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/backup/restore', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
    });
  },

  // Chunked restore (handles ZIPs of any size by uploading 5MB at a time)
  restoreInit: (filename, totalChunks) =>
    api.post('/backup/restore/init', { filename, total_chunks: totalChunks }),
  restoreChunkUpload: (sessionId, index, blob) => {
    const fd = new FormData();
    fd.append('file', blob, `chunk_${index}`);
    return api.put(`/backup/restore/chunk/${sessionId}/${index}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  restoreChunkedValidate: (sessionId) =>
    api.post(`/backup/restore/validate/${sessionId}`, null, { timeout: 600000 }),
  restoreChunkedFinalize: (sessionId) =>
    api.post(`/backup/restore/finalize/${sessionId}`),
  restoreChunkedProgress: (sessionId) =>
    api.get(`/backup/restore/progress/${sessionId}`),
  restoreChunkedCleanup: (sessionId) =>
    api.delete(`/backup/restore/${sessionId}`),
};

// Setup API
export const setupAPI = {
  initial: () => api.post('/setup/initial'),
};

// Google Calendar API
export const googleCalendarAPI = {
  // OAuth
  getStatus: () => api.get('/oauth/calendar/status'),
  getAuthUrl: () => api.get('/oauth/calendar/authorize'),
  disconnect: () => api.post('/oauth/calendar/disconnect'),
  
  // Calendars
  listCalendars: () => api.get('/calendar/google/calendars'),
  configure: (calendarId, locationId, autoSync = false) => 
    api.post('/calendar/google/configure', null, { 
      params: { calendar_id: calendarId, location_id: locationId, auto_sync: autoSync } 
    }),
  
  // Sync
  sync: (daysAhead = 30) => api.post('/calendar/google/sync', null, { params: { days_ahead: daysAhead } }),
  getEvents: (daysAhead = 7) => api.get('/calendar/google/events', { params: { days_ahead: daysAhead } }),
};

// Notifications API
export const notificationsAPI = {
  // Preferences
  getPreferences: () => api.get('/notifications/preferences'),
  updatePreferences: (preferences) => api.put('/notifications/preferences', preferences),
  resetPreferences: () => api.post('/notifications/preferences/reset'),
  
  // Notifications
  getAll: (unreadOnly = false, limit = 50) => 
    api.get('/notifications', { params: { unread_only: unreadOnly, limit } }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (notificationId) => api.post(`/notifications/${notificationId}/read`),
  markAllAsRead: () => api.post('/notifications/read-all'),
  delete: (notificationId) => api.delete(`/notifications/${notificationId}`),
  deleteOld: (days = 30) => api.delete('/notifications/old', { params: { days } }),

  // SSE Stream URL
  getStreamUrl: () => {
    const token = localStorage.getItem('token');
    return `${API_URL}/notifications/stream?token=${token}`;
  },
};

// Pending Procedures API (Biopsy/AP tracking)
export const pendingProceduresAPI = {
  // Procedure Types (configurable)
  getTypes: (category = null, includeInactive = false) => 
    api.get('/pending-procedures/types', { params: { category, include_inactive: includeInactive } }),
  createType: (data) => api.post('/pending-procedures/types', data),
  updateType: (typeId, data) => api.put(`/pending-procedures/types/${typeId}`, data),
  deleteType: (typeId) => api.delete(`/pending-procedures/types/${typeId}`),
  reorderTypes: (order) => api.post('/pending-procedures/types/reorder', order),
  
  // Pending Procedures
  getAll: (params = {}) => api.get('/pending-procedures', { params }),
  getSummary: (params = {}) => api.get('/pending-procedures/summary', { params }),
  getByPatient: (patientId, includeResolved = false) => 
    api.get(`/pending-procedures/by-patient/${patientId}`, { params: { include_resolved: includeResolved } }),
  create: (data) => api.post('/pending-procedures', data),
  resolve: (procedureId, data) => api.put(`/pending-procedures/${procedureId}/resolve`, data),
  unresolve: (procedureId) => api.put(`/pending-procedures/${procedureId}/unresolve`),
  delete: (procedureId) => api.delete(`/pending-procedures/${procedureId}`),
};

// Patient Lookup API (public)
export const patientLookupAPI = {
  lookupByDni: (dni, birthdate, phone) => api.get('/patient-lookup/by-dni', { params: { dni, birthdate, phone } }),
  lookupByToken: (token) => api.get(`/patient-lookup/by-token/${token}`),
  generateToken: (patientId, daysValid = 30) => api.post('/patient-lookup/generate-token', { patient_id: patientId, days_valid: daysValid }),
};

// Brochures API
export const brochureAPI = {
  getTemplates: () => api.get('/brochure-templates'),
  createTemplate: (data) => api.post('/brochure-templates', data),
  updateTemplate: (id, data) => api.put(`/brochure-templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/brochure-templates/${id}`),
  duplicateTemplate: (id, overrides = {}) => api.post(`/brochure-templates/${id}/duplicate`, overrides || {}),
  getPatientBrochures: (patientId) => api.get(`/patient-brochures/${patientId}`),
  createBrochure: (data) => api.post('/patient-brochures', data),
  updateBrochure: (id, data) => api.put(`/patient-brochures/${id}`, data),
  deleteBrochure: (id) => api.delete(`/patient-brochures/${id}`),
  getPdf: (id, template, signatureType) => api.get(`/patient-brochures/${id}/pdf`, { params: { template, signature_type: signatureType }, responseType: 'blob' }),
  sendEmail: (id, template, signatureType, overrideEmail) => api.post(`/patient-brochures/${id}/send-email`, null, { params: { template, signature_type: signatureType, ...(overrideEmail ? { override_email: overrideEmail } : {}) } }),
  sendWhatsApp: (id, template, signatureType, overridePhone) => api.post(`/patient-brochures/${id}/send-whatsapp`, null, { params: { template, signature_type: signatureType, ...(overridePhone ? { override_phone: overridePhone } : {}) } }),
  calculateBraSize: (data) => api.post('/brochures/calculate-bra-size', data),
  duplicateBrochure: (id, targetPatientId) => api.post(`/patient-brochures/${id}/duplicate`, { target_patient_id: targetPatientId || null }),
};

// Email Logs API
export const emailLogsAPI = {
  getAll: (params) => api.get('/email-logs', { params }),
  resend: (logId, overrideEmail) => api.post(`/email-logs/${logId}/resend`, { override_email: overrideEmail || null }),
};

// Compliance API (RGPD/LOPDGDD — Art. 28 + Art. 32 + Art. 5.2)
export const complianceAPI = {
  // Security log (immutable, hash-chained)
  listSecurityLog: () => api.get('/compliance/security-log'),
  createSecurityLogEntry: (data) => api.post('/compliance/security-log', data),
  verifySecurityLog: () => api.get('/compliance/security-log/verify'),
  exportSecurityLogPdf: () => api.get('/compliance/security-log/export.pdf', { responseType: 'blob' }),
  exportSecurityLogJson: () => api.get('/compliance/security-log/export.json', { responseType: 'blob' }),
  // Processors (Art. 28)
  listProcessors: () => api.get('/compliance/processors'),
  createProcessor: (data) => api.post('/compliance/processors', data),
  updateProcessor: (id, data) => api.put(`/compliance/processors/${id}`, data),
  deleteProcessor: (id) => api.delete(`/compliance/processors/${id}`),
  exportProcessorsPdf: () => api.get('/compliance/processors/export.pdf', { responseType: 'blob' }),
};

// Surgery Simulation API
export const surgerySimulationAPI = {
  generate: (data) => api.post('/surgery-simulation/generate', data, { timeout: 180000 }),
  generateMulti: (data) => api.post('/surgery-simulation/generate-multi', data, { timeout: 600000 }),
  morph: (data) => api.post('/surgery-simulation/morph', data, { timeout: 30000 }),
  morphPreview: (data) => api.post('/surgery-simulation/morph-preview', data, { timeout: 10000 }),
  exportSnapshotsPDF: (data) => api.post('/surgery-simulation/export-snapshots-pdf', data, { timeout: 30000, responseType: 'blob' }),
  emailSnapshotsPDF: (data) => api.post('/surgery-simulation/email-snapshots-pdf', data, { timeout: 30000 }),
  morphCompare: (data) => api.post('/surgery-simulation/morph-compare', data, { timeout: 60000 }),
  drawScars: (data) => api.post('/surgery-simulation/draw-scars', data, { timeout: 15000 }),
  getPatientSimulations: (patientId) => api.get(`/surgery-simulation/patient/${patientId}`),
  deleteSimulation: (id) => api.delete(`/surgery-simulation/${id}`),
  // Presets
  getPresets: () => api.get('/surgery-simulation/presets/list'),
  createPreset: (data) => api.post('/surgery-simulation/presets', data),
  updatePreset: (id, data) => api.put(`/surgery-simulation/presets/${id}`, data),
  deletePreset: (id) => api.delete(`/surgery-simulation/presets/${id}`),
  sharePreset: (id, data) => api.post(`/surgery-simulation/presets/${id}/share`, data),
  duplicatePreset: (id) => api.post(`/surgery-simulation/presets/${id}/duplicate`),
  getShareableUsers: () => api.get('/surgery-simulation/presets/users'),
};

export const certificateAPI = {
  getAll: (patientId) => api.get(`/medical-certificates/${patientId}`),
  create: (data) => api.post('/medical-certificates', data),
  update: (id, data) => api.put(`/medical-certificates/${id}`, data),
  delete: (id) => api.delete(`/medical-certificates/${id}`),
  getPdf: (id, template = 'v1_private') => `${api.defaults.baseURL}/medical-certificates/${id}/pdf?template=${template}`,
  sendEmail: (id, template, overrideEmail) => {
    const params = new URLSearchParams({ template });
    if (overrideEmail) params.append('override_email', overrideEmail);
    return api.post(`/medical-certificates/${id}/send-email?${params}`);
  },
  getMacros: (type) => api.get('/certificate-macros', { params: { certificate_type: type } }),
  createMacro: (data) => api.post('/certificate-macros', data),
  updateMacro: (id, data) => api.put(`/certificate-macros/${id}`, data),
  deleteMacro: (id) => api.delete(`/certificate-macros/${id}`),
};

// Review Request API
export const reviewAPI = {
  getStatus: (patientId) => api.get(`/reviews/status/${patientId}`),
  request: (patientId, method, language = 'es') => api.post('/reviews/request', { patient_id: patientId, method, language }),
  markWritten: (patientId) => api.post(`/reviews/mark-written/${patientId}`),
  getStats: (params) => api.get('/reviews/stats', { params }),
  bulkRequest: (patientIds, method, language = 'es') => api.post('/reviews/bulk-request', { patient_ids: patientIds, method, language }),
};


// Motiva Implant Catalog API
export const motivaCatalogAPI = {
  list: (params = {}) => api.get('/motiva-catalog', { params }),
  listLines: () => api.get('/motiva-catalog/lines'),
  get: (ref) => api.get(`/motiva-catalog/${ref}`),
  create: (data) => api.post('/motiva-catalog', data),
  update: (ref, data) => api.put(`/motiva-catalog/${ref}`, data),
  delete: (ref) => api.delete(`/motiva-catalog/${ref}`),
  reseed: () => api.post('/motiva-catalog/reseed'),
};

// Unified Implant Search — across Mentor, Polytech, Silimed, Motiva
export const implantsAPI = {
  search: (params = {}) => api.get('/implants/search', { params }),
  brands: () => api.get('/implants/brands'),
  // Catalog-level brand → product-lines map (used by the dynamic dropdowns
  // in the Implant Calculator)
  catalogBrands: () => api.get('/implant-catalog/brands'),
  exportExcel: (params = {}) =>
    api.get('/implants/export/excel', { params, responseType: 'blob' }),
  seedMentorFromPdf: () => api.post('/implant-catalog/seed-mentor-from-pdf'),
  seedPolytechFromPdf: () => api.post('/implant-catalog/seed-polytech-from-pdf'),
  seedSilimedFromPdf: () => api.post('/implant-catalog/seed-silimed-from-pdf'),
  silimedAudit: () => api.get('/implant-catalog/silimed-audit'),
  catalogAudit: (brand) => api.get('/implant-catalog/audit', { params: { brand } }),
};

// Per-doctor implant favorites + add-to-surgery helper
export const implantFavoritesAPI = {
  list: () => api.get('/implants/favorites'),
  refs: () => api.get('/implants/favorites/refs'),
  add: (payload) => api.post('/implants/favorites', payload),
  remove: (brand, ref) =>
    api.delete(`/implants/favorites/${encodeURIComponent(brand)}/${encodeURIComponent(ref)}`),
  addToSurgery: (payload) => api.post('/implants/add-to-surgery', payload),
};

// Per-doctor surgery templates (saved bundles of implants + defaults)
export const surgeryTemplatesAPI = {
  list: () => api.get('/surgery-templates'),
  get: (id) => api.get(`/surgery-templates/${id}`),
  create: (payload) => api.post('/surgery-templates', payload),
  update: (id, payload) => api.put(`/surgery-templates/${id}`, payload),
  remove: (id) => api.delete(`/surgery-templates/${id}`),
  apply: (id, payload) => api.post(`/surgery-templates/${id}/apply`, payload),
};

export const surgeryBudgetsAPI = {
  // Procedures CRUD (replaces old interventions + anesthesia split)
  getProcedures: (includeInactive = false) => api.get('/surgery-budgets/config/procedures', { params: { include_inactive: includeInactive } }),
  createProcedure: (payload) => api.post('/surgery-budgets/config/procedures', payload),
  updateProcedure: (code, payload) => api.put(`/surgery-budgets/config/procedures/${code}`, payload),
  deleteProcedure: (code, hard = false) => api.delete(`/surgery-budgets/config/procedures/${code}`, { params: { hard } }),
  // Hospital pricing
  getOrTiers: () => api.get('/surgery-budgets/config/or-tiers'),
  setOrTier: (tierCode, payload) => api.put(`/surgery-budgets/config/or-tiers/${tierCode}`, payload),
  getStays: () => api.get('/surgery-budgets/config/stays'),
  setStay: (stayCode, payload) => api.put(`/surgery-budgets/config/stays/${stayCode}`, payload),
  getTests: () => api.get('/surgery-budgets/config/tests'),
  setTest: (testCode, payload) => api.put(`/surgery-budgets/config/tests/${testCode}`, payload),
  // Silimed implant family pricing
  getImplantFamilies: () => api.get('/surgery-budgets/config/implant-families'),
  setImplantFamily: (familyCode, payload) => api.put(`/surgery-budgets/config/implant-families/${familyCode}`, payload),
  resolveImplantReference: (reference) => api.get('/surgery-budgets/config/implant-family-resolve', { params: { reference } }),
  seedDefaults: () => api.post('/surgery-budgets/config/seed-defaults'),
  dedupe: () => api.post('/surgery-budgets/config/dedupe'),
  // Generation + persistence
  preview: (payload) => api.post('/surgery-budgets/preview', payload),
  save: (payload) => api.post('/surgery-budgets/save', payload),
  list: (patientId) => api.post('/surgery-budgets/list', null, { params: patientId ? { patient_id: patientId } : {} }),
  // PDF download (lang = 'es' | 'en')
  pdfUrl: (budgetId, lang = 'es') => `/api/surgery-budgets/${budgetId}/pdf?lang=${lang}`,
  downloadPdf: (budgetId, lang = 'es') => api.get(`/surgery-budgets/${budgetId}/pdf`, { params: { lang }, responseType: 'blob' }),
  // Email-to-patient (Spanish or English; optional override_email)
  emailToPatient: (budgetId, { lang = 'es', overrideEmail } = {}) =>
    api.post(`/surgery-budgets/${budgetId}/email-to-patient`, null, {
      params: {
        lang,
        ...(overrideEmail ? { override_email: overrideEmail } : {}),
      },
    }),
  // Bind / re-bind a patient to an existing budget (anonymous → identified)
  assignPatient: (budgetId, patientId) =>
    api.patch(`/surgery-budgets/${budgetId}/assign-patient`, { patient_id: patientId }),
  // Hard-delete a saved budget (creator or superadmin only).
  delete: (budgetId) => api.delete(`/surgery-budgets/${budgetId}`),
  // Mark / unmark a budget as accepted manually (useful when the patient
  // signs in-consult and hence never triggers the public accept flow).
  markAccepted: (budgetId, accepted = true) =>
    api.post(`/surgery-budgets/${budgetId}/mark-accepted`, { accepted }),
  // Retroactive marker for Stripe payment requests done before automatic
  // tracking existed. Does NOT send any message — just bumps counter.
  markStripeRequested: (budgetId, increment = 1) =>
    api.post(`/surgery-budgets/${budgetId}/mark-stripe-requested`, {
      channel: 'manual', increment,
    }),
  // Backfill: attach the PDF of every existing budget to its patient's
  // medical_files (superadmin only). Idempotent — safe to re-run.
  backfillMedicalFiles: () => api.post('/surgery-budgets/backfill-medical-files'),
};

export const adminBankingAPI = {
  // Per-doctor bank details used by the surgery-budget PDF
  update: (payload) => api.put('/admin/profile/banking', payload),
};

// Kiosk Mode — persistent signature-pad screen for tablets (e.g. iPad 3)
export const kioskAPI = {
  list: (doctorId) => api.get('/kiosk/devices', { params: doctorId ? { doctor_id: doctorId } : {} }),
  register: (payload) => api.post('/kiosk/devices', payload),
  remove: (id) => api.delete(`/kiosk/devices/${id}`),
  connect: (id) => api.post(`/kiosk/devices/${id}/connect`),
  disconnect: (id) => api.post(`/kiosk/devices/${id}/disconnect`),
  testSound: (id) => api.post(`/kiosk/devices/${id}/test-sound`),
  push: (id, payload) => api.post(`/kiosk/devices/${id}/push`, payload),
  // Public (token-only) — used by the iPad itself
  me: (token) => api.get('/kiosk/me', { params: { token } }),
  queue: (token) => api.get('/kiosk/queue', { params: { token } }),
  pollPush: (token) => api.get('/kiosk/poll-push', { params: { token } }),
  ackPush: (token, pendingId) => api.post('/kiosk/poll-push/ack', { pending_id: pendingId }, { params: { token } }),
  callPatient: (token, appointmentId) => api.post('/kiosk/call-patient', { appointment_id: appointmentId }, { params: { token } }),
  markAttended: (token, appointmentId) => api.post('/kiosk/mark-attended', { appointment_id: appointmentId }, { params: { token } }),
  selfDisconnect: (token) => api.post('/kiosk/self-disconnect', {}, { params: { token } }),
  banner: (token) => api.get('/kiosk/banner', { params: { token } }),
  streamUrl: (token) => `${API_URL}/kiosk/stream?token=${encodeURIComponent(token)}`,
  // Tablet-initiated pairing (see backend /pair-code endpoints)
  requestPairCode: () => api.post('/kiosk/pair-code'),
  pollPairCode: (requestId) => api.get(`/kiosk/pair-code/${requestId}/poll`),
  claimPairCode: (payload) => api.post('/kiosk/pair-code/claim', payload),
  // Reimprimir hoja: repair token TTL 5 min para pareado de un dispositivo
  // de repuesto sin tener que recrear el kiosco (mantiene el token real).
  createRepairToken: (id) => api.post(`/kiosk/devices/${id}/repair-token`),
  consumeRepairToken: (repairToken) => api.post('/kiosk/repair/consume', { repair_token: repairToken }),
  // Kiosk-scoped: generate a mobile-upload token for a patient in the queue
  // so the reception tablet can show them a QR to upload documents.
  mobileUploadTokenFor: (appointmentId, token) => api.post(`/kiosk/mobile-upload-token/${appointmentId}`, null, { params: { token } }),
};

export const tvBannerAPI = {
  list: () => api.get('/admin/tv-banner'),
  create: (payload) => api.post('/admin/tv-banner', payload),
  update: (id, payload) => api.put(`/admin/tv-banner/${id}`, payload),
  remove: (id) => api.delete(`/admin/tv-banner/${id}`),
  uploadImage: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/admin/tv-banner/upload-image', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const whatsappQuietHoursAPI = {
  config: () => api.get('/admin/whatsapp-quiet-hours/config'),
  list: (status) => api.get('/admin/whatsapp-quiet-hours', { params: status ? { status } : {} }),
  flushNow: () => api.post('/admin/whatsapp-quiet-hours/flush-now'),
  cancel: (id) => api.delete(`/admin/whatsapp-quiet-hours/${id}`),
};


export default api;
